// frontend/admin-portal/src/components/procurement/RequisitionModal.jsx
import React, { useEffect, useState } from "react";
import { Modal, Button, Input, Alert } from "@fiberhome/shared-ui";
import { requisitionService } from "../../services/requisitionService";
import RequisitionItemEditor from "./RequisitionItemEditor";

const EMPTY = {
  title: "",
  description: "",
  neededBy: "",
  priority: "normal",
  notes: "",
  items: [],
};

export default function RequisitionModal({
  open,
  requisition,
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    if (requisition) {
      setForm({
        title: requisition.title || "",
        description: requisition.description || "",
        neededBy: requisition.neededBy || "",
        priority: requisition.priority || "normal",
        notes: requisition.notes || "",
        items: (requisition.items || []).map((it) => ({
          productId: it.productId,
          productName: it.product?.name,
          productSku: it.product?.sku,
          freeTextName: it.freeTextName,
          freeTextSpec: it.freeTextSpec,
          quantity: it.quantity,
          unit: it.unit,
          targetPrice: it.targetPrice,
          notes: it.notes,
        })),
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, requisition]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      setError("Title is required");
      return;
    }
    if (form.items.length === 0) {
      setError("Add at least one item");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        title: form.title,
        description: form.description,
        neededBy: form.neededBy || null,
        priority: form.priority,
        notes: form.notes,
        items: form.items.map((it) => ({
          productId: it.productId || null,
          freeTextName: it.freeTextName || null,
          freeTextSpec: it.freeTextSpec || null,
          quantity: Number(it.quantity) || 1,
          unit: it.unit || "pcs",
          targetPrice:
            it.targetPrice === "" || it.targetPrice == null
              ? null
              : Number(it.targetPrice),
          notes: it.notes || null,
        })),
      };
      if (requisition?.id)
        await requisitionService.update(requisition.id, payload);
      else await requisitionService.create(payload);
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={requisition ? `Edit: ${requisition.code}` : "New Requisition"}
      size="xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={saving}>
            {requisition ? "Save" : "Create"}
          </Button>
        </>
      }
    >
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <div className="row g-3 mb-3">
        <div className="col-md-8">
          <Input
            label="Title *"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
          />
        </div>
        <div className="col-md-4">
          <label className="fh-input-label">Priority</label>
          <select
            className="fh-input"
            value={form.priority}
            onChange={(e) => set("priority", e.target.value)}
          >
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
        <div className="col-md-6">
          <Input
            label="Needed By"
            type="date"
            value={form.neededBy}
            onChange={(e) => set("neededBy", e.target.value)}
          />
        </div>
        <div className="col-md-6">
          <Input
            label="Description"
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
          />
        </div>
        <div className="col-12">
          <Input
            label="Notes"
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
          />
        </div>
      </div>

      <h6 className="mb-2">Items</h6>
      <RequisitionItemEditor
        items={form.items}
        onChange={(v) => set("items", v)}
      />
    </Modal>
  );
}
